import type { AxiosRequestConfig, AxiosResponse } from 'axios';
import _ from 'lodash';
import { Api } from '../config/axiosConfig';
import { logger } from '../config/loggers';
import { captureMessage } from './logging/exception.utils';

/* types */
type ServiceArgument<D = any> =
  | string
  | number
  | boolean
  | Partial<AxiosRequestConfig<D>>;

export interface ServiceConfig extends AxiosRequestConfig {
  url: string;
}

const defaultStaticConfig: Partial<AxiosRequestConfig> = {
  method: 'get',
  // paramsSerializer: standardQuerySerializer,
  // transformRequest: [ignoreUndefineds],
};

/**
 * @name ServiceHandler
 * @description Creates and returns a static endpoint definition (function) for reuse.
 * @param staticConfig Any available axios config options
 * @param instance A valid axios instance
 * @returns function(...args?: string | number, axiosConfig?: AxiosConfig{})
 * @example const getTodo = ServiceHandler({ url: '/api/todos/:id' })
 * @example await getTodo('123456');
 */
export const ServiceHandler = <Request = any, Response = any>(
  staticConfig: ServiceConfig,
  instance = Api
) => {
  return (...args: ServiceArgument<Request>[]) => {
    const config = args.reduce(deriveHandlerConfig, {
      ...defaultStaticConfig,
      ...staticConfig,
      url: staticConfig.url,
    });

    return instance.request<
      Response,
      AxiosResponse<Response, Request>,
      Request
    >(config);
  };
};

function deriveHandlerConfig(
  acc: ServiceConfig,
  arg: ServiceArgument,
  index: number,
  array: ServiceArgument[]
): ServiceConfig {
  // A path param is a string, a number or a boolean (`/favorite/:flag` takes true or false).
  if (
    typeof arg === 'string' ||
    typeof arg === 'number' ||
    typeof arg === 'boolean'
  ) {
    return {
      ...acc,
      url: acc.url.replace(/:[a-z]+/i, arg.toString()),
    };
  }

  const isLast: boolean = index === array.length - 1;
  if (isLast && typeof arg === 'object' && arg !== null) {
    // Handle the case where data is passed as { data: payload }
    if (arg.data !== undefined) {
      return {
        ...acc,
        ...arg,
        data: arg.data, // Ensure data is properly set in the axios config
      };
    }
    return { ...acc, ...arg };
  }

  // Invalid argument — almost always an undefined/null path param from a
  // caller that fired before its data resolved. Report it for observability
  // but don't crash the app: skip the argument and let the resulting request
  // fail softly (the caller's error handling takes over).
  reportInvalidHandlerArgument(arg, index, acc.url);
  return acc;
}

/**
 * @name reportInvalidHandlerArgument
 * @description Logs and reports (to Sentry) an argument that a ServiceHandler
 * could not turn into a path param or config. The Sentry message is keyed by
 * url + position so occurrences group into one actionable issue per endpoint
 * rather than one per call.
 */
function reportInvalidHandlerArgument(
  arg: ServiceArgument,
  index: number,
  url: string
): void {
  const message = `ServiceHandler: invalid argument at position ${index} for "${url}"`;

  logger.warn(message, { index, url, argType: typeof arg, arg });
  captureMessage(message, {
    level: 'warning',
    extra: { index, url, argType: typeof arg, argValue: String(arg) },
  });
}

export function applyQueryConfig(MAP: any[], params: any) {
  const nextParams: any = {};

  for (let i = 0; i < MAP.length; i += 1) {
    const config = MAP[i];

    // key in the final payload
    let paramKey = config.path;
    if (config.paramKey) {
      paramKey =
        typeof config.paramKey === 'function'
          ? config.paramKey(params)
          : config.paramKey;
    }

    // final value, transformed or not
    const value =
      typeof config.transform === 'function'
        ? config.transform(params)
        : params[config.path];

    // gives us a way to omit undefineds from final nextParams
    if (value || _.isNumber(value)) {
      nextParams[paramKey] = value;
    }
  }

  return nextParams;
}

/**
 * Parses legacy stringified JSON response bodies before serializers
 * that expect parsed objects (for example serializePageableResponse).
 */
export function parseStringifiedJsonResponse(data: unknown) {
  if (typeof data !== 'string') {
    return data;
  }
  try {
    return JSON.parse(data);
  } catch {
    return JSON.parse(JSON.stringify(data));
  }
}

/**
 * @name responseStringTransformer
 * @deprecated Use {@link parseStringifiedJsonResponse} instead.
 */
export function responseStringTransformer(data: string) {
  return parseStringifiedJsonResponse(data);
}

export function serializeStandardResponse(data: string) {
  try {
    return {
      data: JSON.parse(data),
    };
  } catch {
    return {
      data: JSON.parse(JSON.stringify(data)),
    };
  }
}

export function stringifyRequestBody(data: any) {
  return JSON.stringify(data);
}

/**
 * @name makeCancelable
 * @description Wraps a {@link ServiceHandler} function so that each call aborts
 * the previous still-in-flight call for the same handler instance. This
 * replaces the old `ServiceHandlerFactory({ isCancelable: true })` behavior:
 * the shared `Api` response interceptor turns the resulting `CanceledError`
 * into a rejected `Error('Request Cancelled')`, exactly as before.
 *
 * The abort signal is injected into the handler's final axios-config argument
 * (or appended as one when the handler is called with only path args), so
 * callers keep their existing signatures.
 *
 * A caller that passes its own `signal` (React Query's, say) owns cancellation for that call: it neither aborts
 * the previous call nor can be aborted by the next one, so several searches against one endpoint can run at once.
 *
 * @example
 * const _search = makeCancelable(ServiceHandler({ url: '/api/x/search' }));
 * export const search = (params = {}) => _search({ params });
 */
export function makeCancelable<Fn extends (...args: any[]) => Promise<any>>(
  handler: Fn
): Fn {
  let controller: AbortController | null = null;

  const cancelable = (...args: any[]) => {
    const last = args[args.length - 1];
    if (last && typeof last === 'object' && last.signal) {
      return handler(...args);
    }
    if (controller) {
      controller.abort();
    }
    controller = new AbortController();
    const { signal } = controller;

    if (last && typeof last === 'object' && !Array.isArray(last)) {
      const nextArgs = args.slice(0, -1);
      nextArgs.push({ ...last, signal });
      return handler(...nextArgs);
    }

    return handler(...args, { signal });
  };

  return cancelable as Fn;
}
